import { useState, useRef, useCallback } from 'preact/hooks';
import type { ImageAttachment } from '@voice-assistant/shared';
import {
  isSupportedImage,
  compressImage,
  createThumbnail,
  perImageBudget,
  checkTotalImageBudget,
} from '../utils/imageUtils';

export interface UseAttachmentsResult {
  attachments: ImageAttachment[];
  hasAttachments: boolean;
  processing: boolean;
  error: string | null;
  triggerFilePicker: () => void;
  removeAttachment: (index: number) => void;
  clearAttachments: () => void;
  /** Hidden file input — render this in your component tree */
  fileInputProps: {
    ref: preact.RefObject<HTMLInputElement>;
    type: 'file';
    accept: string;
    multiple: boolean;
    style: { display: 'none' };
    onChange: (e: Event) => void;
  };
}

export function useAttachments(): UseAttachmentsResult {
  const [attachments, setAttachments] = useState<ImageAttachment[]>([]);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const triggerFilePicker = useCallback(() => {
    inputRef.current?.click();
  }, []);

  const handleFiles = useCallback(async (e: Event) => {
    const input = e.target as HTMLInputElement;
    const files = input.files;
    if (!files || files.length === 0) return;

    setProcessing(true);
    setError(null);

    try {
      const newAttachments: ImageAttachment[] = [];

      // Per-image budget shrinks as N grows so the aggregate stays under the
      // per-message SUM budget (SDK crashes at ~500KB per stdin line — issue
      // #1165). `attachments.length` already-staged + this batch.
      const totalImages = attachments.length + files.length;
      const budget = perImageBudget(totalImages);

      for (let i = 0; i < files.length; i++) {
        const file = files[i];

        if (!isSupportedImage(file)) {
          setError(`"${file.name}" is not a supported image type. Use JPEG, PNG, GIF, or WebP.`);
          continue;
        }

        const [compressed, thumbnail] = await Promise.all([
          compressImage(file, 800, 0.65, budget),
          createThumbnail(file),
        ]);

        newAttachments.push({
          data: compressed.base64,
          mimeType: compressed.mimeType,
          fileName: file.name,
          thumbnailDataUri: thumbnail,
        });
      }

      if (newAttachments.length > 0) {
        // Cumulative per-message SUM budget (issue #1165): images staged
        // earlier were compressed under a LARGER per-image share, so adding
        // more can push the total over the single-JSON-line budget even
        // though each new image fits its own share. Refuse the attach (the
        // existing `error` surface renders it) — already-staged attachments
        // are kept untouched.
        const verdict = checkTotalImageBudget([
          ...attachments.map((a) => a.data.length),
          ...newAttachments.map((a) => a.data.length),
        ]);
        if (!verdict.allowed) {
          setError(
            `Attachments total ${(verdict.totalBytes / 1024).toFixed(0)}KB — over the ${(verdict.budgetBytes / 1024).toFixed(0)}KB per-message limit. Remove an attachment or add smaller images.`,
          );
          return;
        }
        setAttachments((prev) => [...prev, ...newAttachments]);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to process image');
    } finally {
      setProcessing(false);
      // Reset input so picking the same file again triggers onChange
      if (inputRef.current) inputRef.current.value = '';
    }
    // `attachments` (not `.length`): the cumulative budget reads staged data lengths
  }, [attachments]);

  const removeAttachment = useCallback((index: number) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const clearAttachments = useCallback(() => {
    setAttachments([]);
    setError(null);
  }, []);

  return {
    attachments,
    hasAttachments: attachments.length > 0,
    processing,
    error,
    triggerFilePicker,
    removeAttachment,
    clearAttachments,
    fileInputProps: {
      ref: inputRef,
      type: 'file' as const,
      accept: 'image/jpeg,image/png,image/gif,image/webp',
      multiple: true,
      style: { display: 'none' } as const,
      onChange: handleFiles,
    },
  };
}
